import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Panel, InputGroup, Glyphicon, Modal, Alert, DropdownButton, MenuItem, Well } from 'react-bootstrap';
import _ from 'lodash';
import Moment from 'moment';
import chrono from 'chrono-node';
import { browserHistory } from 'react-router';
import { DragSource, DropTarget } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend'
import DatePicker from 'react-datepicker';
import { isEmptyObject, isEmptyString } from '../../validators/formValidators';
import { getObjectValue,
         extractFields, 
         updateTask,
         getTodayDateTZ, 
         formatDateTime,
         getDateTimeFromUtcInLocalTime,
         getDateTimeUTC } from '../../helpers/utils';
    
import { fieldSource,
         fieldTarget,
         collectSource,
         collectTarget } from '../../../client/dnd';
        
import TextInput from './TextInput';
import Select from './Select';
import Button from './Button';
import TextArea from './TextArea';
import DateInput from './DateInput';
import CheckBox from './CheckBox';
import SendEmailForm from './SendEmailForm';
import { validateTaskField } from './TaskEditor';
import { caseSetServerError } from './../../actions/caseActions';
import { dialogHeaderFooterBgColor, errorBackgroundColor, warningBackgroundColor } from '../../helpers/colors';
import ServerError from './ServerError';
import { wrapEmployeeForSelect } from './DynamicFields';


// Drag sources and drop targets only interact
// if they have the same string type.
export const TASK_FIELD = 'TASK_FIELD';

export const updateTaskDatesForCase = (currentCase, task, user, isEmptyError = true) => {
    if (task.error) {
        delete task.error['Chrono'];
    }

    if (!task['DeadlineType']) {
        task['DeadlineType'] = deadlineType;
    }

    delete task['DateTimeStart'];
    delete task['DateTimeEnd'];
    task['DeadlineType'].showDateEnd = false;
    task['DeadlineType'].showDateStart = false;
    task['DeadlineType'].showTime = false;
    let referenceDate = task['ChronoReference'];
    if (currentCase) {
        if (!referenceDate || referenceDate === 'task.CreatedDate') {
            if (task['created_at']) {
                referenceDate = getDateTimeFromUtcInLocalTime(task['created_at'], user);
            } else {
                referenceDate = getTodayDateTZ(user, true, true);
            }
        } else {
            referenceDate = getObjectValue(currentCase, referenceDate);
        }
    }

    if ((!referenceDate || isEmptyString(referenceDate))) {
        if (!task.error) {
            task.error = {};
        }

        if (isEmptyError && (isEmptyString(task['BlockedBy']) || task['BlockedBy'] === 0)) {
            task.error['Chrono'] = `${task['ChronoReference']} is empty.`;
        }

        const parsed = new chrono.parse(task['Chrono']);
        if (parsed.length === 0) {
            task.error['Chrono'] = 'Invalid date/time/range specification.';
        }

        return;
    }

    referenceDate = getDateTimeUTC(referenceDate, user['Timezone'], true).toDate();
    const parsed = new chrono.parse(task['Chrono'], referenceDate);
    if (parsed.length > 0) {
        const startDate = formatDateTime(parsed[0].start.date());
        let endDate;
        if (parsed[0].end) {
            endDate = formatDateTime(parsed[0].end.date());
        }

        task['DeadlineType'].showTime = true;
        if (endDate) {
            task['DateTimeStart'] = startDate;
            task['DateTimeEnd'] = endDate;
            task['DeadlineType'].showDateStart = true;
            task['DeadlineType'].showDateEnd = true;
        } else {
            task['DateTimeEnd'] = startDate;
            task['DeadlineType'].showDateEnd = true;
        }

        if (task.error) {
            delete task.error['Chrono'];
        }
    } else {
        if (!task.error) {
            task.error = {};
        }

        if (isEmptyString(task['Chrono'])) {
            if (isEmptyError) {
                task.error['Chrono'] = 'Cannot be empty';
            }
        } else {
            task.error['Chrono'] = 'Invalid date/time/range specification.';
        }
    }
};

const deadlineType = {
    showDateStart: false,
    showDateEnd: false,
    showTime: true,
};

const DEFAULT_FIELD = {
    id: undefined,
    CaseId: undefined,
    Completed: false,
    Title: '{extra.ClientFullName} - [CHANGE ME]',
    DeadlineType: deadlineType,
    Description: '',
    DateTimeStart: undefined,
    DateTimeEnd: undefined,
    Chrono: '',
    ChronoReference: null,
    BlockedBy: undefined,
    OrderId: undefined,
    AssignedTo: undefined,
    version: 0,
    $Editing$: false,
    $Deleted$: false,
    $Modified$: false,
    $AssignedToChanged$: true,
    ActionType: 'none',
    ActionTrigger: 'completed',
};

export const getDefaultField = (id, AssignedTo = undefined, CaseId = undefined) => {
    const ChronoReference = 'task.CreatedDate';
    const field = {
        ...DEFAULT_FIELD,
        AssignedTo,
        id,
        OrderId: id,
        CaseId,
        ChronoReference,
        hasFocus: false,
    };

    return field;
};


class TaskEditorField extends PureComponent {
    constructor(props) {
        super(props);
        this.onChange = this.onChange.bind(this);
        this.onCaseIdNavigate = this.onCaseIdNavigate.bind(this);
        this.updateActionFields = this.updateActionFields.bind(this);
        this.renderWhenComponent = this.renderWhenComponent.bind(this);
        this.renderTaskMainComponent = this.renderTaskMainComponent.bind(this);
        this.renderDetailsComponent = this.renderDetailsComponent.bind(this);
        this.collapseFields = this.collapseFields.bind(this);
        this.state = {
            field: getDefaultField(undefined),
            loading: false,
        };

        this.regularDateRe = /^\s*[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}\s*$/;
    }

    componentDidMount() {
        const field = { ...this.props.initialField };
        field['DeadlineType'] = field['DeadlineType'] || deadlineType;
        this.setState({ field });
        // Use empty image as a drag preview so browsers don't draw it
        // and we can draw whatever we want on the custom drag layer instead.
        this.props.connectDragPreview(getEmptyImage(), {
            // IE fallback: specify that we'd rather screenshot the node
            // when it already knows it's being dragged so we can hide it with CSS.
            captureDraggingState: true,
        });
    }

    componentWillReceiveProps(nextProps) {
        const field = { ...nextProps.initialField };
        field['DeadlineType'] = field['DeadlineType'] || deadlineType;
        const validate = nextProps.dialogOpen && !this.props.dialogOpen;
        this.setState({ field }, () => {
            if (validate) {
                this.onChange(undefined);
            }
        });
    }

    onCaseIdNavigate(evt) {
        evt.preventDefault();
        browserHistory.push(`/case/edit/intake/${this.state.field['CaseId']}`);
    }

    onChange(evt) {
        const field = this.state.field;
        const oldActionType = field['ActionType'];
        if (evt) {
            const fieldName = evt.target.name;
            let value = evt.target.value;
            switch (fieldName) {
            case 'AssignedTo':
                value = parseInt(value, 10);
                if (isNaN(value)) {
                    value = '';
                }

                field['AssignedTo'] = value;
                field['$AssignedToChanged$'] = true;
                break;

            case 'Completed':
                field['Completed'] = evt.target.checked;
                delete field['ActionPerformed'];
                updateTaskDatesForCase(this.props.currentCase, field, this.props.loggedIn, this.props.isEmptyChronoError);
                break;

            case 'ChronoReference':
                field['ChronoReference'] = value;

                // fallthrough;

            case 'Chrono':
                if (fieldName === 'Chrono') {
                    field['Chrono'] = value;
                }

                if (field.error) {
                    delete field.error['Chrono'];
                }

                if (!field['ChronoReference']) {
                    field['ChronoReference'] = 'task.CreatedDate';
                }

                updateTaskDatesForCase(this.props.currentCase, field, this.props.loggedIn, this.props.isEmptyChronoError);
                break;

            case 'Title':
                field['Title'] = value;
                if (field.error) {
                    delete field.error['Title'];
                }

                if (value.length >= 255) {
                    if (!field.error) {
                        field.error = {};
                    }

                    field.error['Title'] = 'Too long (255 characters max).';
                }

                break;

            case 'BlockedBy':
                field['BlockedBy'] = value;
                if (value === 0) {
                    delete field['BlockedBy'];
                } else {
                    delete field['ActionTrigger'];
                }

                break;

            default:
                field[fieldName] = value;
                break;
            }
        } else {
            if (!isEmptyObject(field.error)) {
                delete field.error['Chrono'];
            }

            updateTaskDatesForCase(this.props.currentCase, field, this.props.loggedIn, this.props.isEmptyChronoError);
            if (field.error) {
                delete field.error['Title'];
            }

            if (field['Title'].length >= 255) {
                if (!field.error) {
                    field.error = {};
                }

                field.error['Title'] = 'Too long (255 characters max).';
            }
        }

        if (field.error) {
            delete field.error['DateTimeStart'];
        }

        if (field['DeadlineType'].showDateStart && field['DeadlineType'].showDateEnd) {
            const startDate = Moment.tz(field['DateTimeStart'], this.props.loggedIn && this.props.loggedIn['Timezone']);
            const endDate = Moment.tz(field['DateTimeEnd'], this.props.loggedIn && this.props.loggedIn['Timezone']);
            if (startDate.isAfter(endDate)) {
                if (!field.error) {
                    field.error = {};
                }

                field.error['DateTimeStart'] = 'The start date is after the end date.';
            }
        }

        this.updateActionFields(field, oldActionType);
        if (this.props.onChange) {
            this.props.onChange(field);
        }
    }

    updateActionFields(field, oldActionType) {
        let actionType = field['ActionType'];
        if (field.error && field.error['ActionType']) {
            delete field.error['ActionType'];
        }

        field['ActionPerformed'] = false;
        // if (!field['DateTimeEnd'] && field['ActionTrigger'] === 'due_datetime') {
        //     field['ActionTrigger'] = 'completed';
        // }
        
        // TODO: Action trigger run automatically on a given date
        field['ActionTrigger'] = 'completed';

        if (actionType == 'send_email' && (isEmptyObject(field['ActionEmail']) || !field['ActionEmail']['Subject'] || !field['ActionEmail']['Message'])) {
            if (!field.error) {
                field.error = {};
            }

            field.error['ActionType'] = 'Email cannot be empty.';
        }
        
        if (oldActionType === actionType) {
            return;
        }
        
        if (actionType === 'none') {
            delete field['ActionTrigger'];
            delete field['ActionEmail'];
            delete field['ActionCaseState'];
            return;
        }

        if (actionType === 'set_case_state') {
            field['ActionTrigger'] = 'completed';
            field['ActionCaseState'] = 'Lead';
            delete field['ActionEmail'];
            return;
        }

        if (actionType === 'send_email') {
            field['ActionTrigger'] = 'completed';
            delete field['ActionCaseState'];
        }
    }

    renderWhenComponent() {
        const {
            disabled,
            dateFields,
            editMode = false,
        } = this.props;

        const field = this.state.field;
        let when = '';
        const showTimeRange = (field['DeadlineType'].showDateStart || (field['DateTimeStart'] && !isEmptyString(field['DateTimeStart']))) &&
                              (field['DeadlineType'].showDateEnd || (field['DateTimeEnd'] && !isEmptyString(field['DateTimeEnd'])));

        let isRegularDate = false;
        if (field['Chrono']) {
            isRegularDate = this.regularDateRe.test(field['Chrono']);
        }

        const chronoHelp = 'Select a date or describe by plain english - eg. "tomorrow", "in 5 hours", "in a week", "friday at 5pm", "next tuesday at 5pm - 7pm".';
        return (
        <div>
        <Row>
            <Col xs={12} id="tour-case-action-task-date">
            <TextInput field={{
                            name: 'Chrono',
                            error: field.error ? field.error['Chrono'] || field.error['DateTimeStart'] || field.error['DateTimeEnd'] : undefined,
                            touched: true,
                            value: field['Chrono'],
                            onChange: this.onChange,
                        }}
                        label="Date"
                        disabled={disabled}
                        name="Chrono"
                        afterComponent={(
                            <InputGroup.Button>
                            <DatePicker onChange={(value) => {
                                            const dateFromValue = new Moment.utc(value).format(process.env.DATE_FORMAT);
                                            this.onChange({ target: { name: 'Chrono', value: dateFromValue } });
                                        }}
                                        todayButton="Today"
                                        dateFormat="MM/dd/yyyy"
                                        showMonthDropdown
                                        showYearDropdown
                                        customInput={<Button id="tour-case-action-task-date-btn" icon="calendar" />}
                                        popperModifiers={{
                                        offset: {
                                            enabled: true,
                                            offset: '-250px, 0px',
                                        },
                                        flip: { enabled: false },
                                        placement: 'bottom-start',
                                        placements: ['bottom'],
                                        preventOverflow: { enabled: false }
                                    }}

                            >
                            <Alert bsStyle="warning">
                            <span style={{
                                wordBreak: 'break',
                                whiteSpace: 'normal',
                                color: '#000',
                            }}>{chronoHelp}</span>
                            </Alert>
                            </DatePicker>
                            <DropdownButton pullRight
                                            style={{
                                                padding: 0,
                                                marginLeft: -8,
                                                height: 35,
                                                width: 20,
                                                backgroundColor: (field['ChronoReference'] && field['ChronoReference'] !== 'task.CreatedDate') ? warningBackgroundColor : undefined
                                            }}>
                            <MenuItem header><h6>Interpret date relative to:</h6></MenuItem>
                            <MenuItem divider />
                            {dateFields.map(dateField => (
                                <MenuItem onClick={() => this.onChange({target: {name:'ChronoReference', value: dateField.value}})}
                                          active={dateField.value === field['ChronoReference']}
                                >
                                {dateField.label}
                                </MenuItem>
                            ))}
                            </DropdownButton>
                            </InputGroup.Button>
                        )}
                        help={chronoHelp}
            />
            </Col>
        </Row>
        {editMode && field['DeadlineType'].showDateStart && !isEmptyString(field['DateTimeStart']) &&
         field['DateTimeStart'] !== 'Invalid date' && field['DateTimeStart'] !== 'InvalidDate' &&
            (<Row>
                <Col xs={12}>
                    <DateInput field={{
                                    name: 'DateTimeStart',
                                    error: field.error ? field.error['DateTimeStart'] : undefined,
                                    touched: true,
                                    value: field['DateTimeStart'] && getDateTimeUTC(field['DateTimeStart'], this.props.loggedIn && this.props.loggedIn['Timezone'], true).toDate(),
                                }}
                                time={field['DeadlineType'].showTime}
                                label="From"
                                disabled
                                name="DateTimeStart"
                                help="From what date (time) does the task require attention."
                    />
                </Col>
                </Row>)}
        {!isRegularDate && editMode && field['DeadlineType'].showDateEnd && !isEmptyString(field['DateTimeEnd']) &&
         field['DateTimeEnd'] !== 'Invalid date' && field['DateTimeEnd'] !== 'InvalidDate' &&
        (<Row>
            <Col xs={12}>
                <DateInput field={{
                                name: 'DateTimeEnd',
                                error: field.error ? field.error['DateTimeEnd'] : undefined,
                                touched: true,
                                value: field['DateTimeEnd'] && getDateTimeUTC(field['DateTimeEnd'], this.props.loggedIn && this.props.loggedIn['Timezone'], true).toDate(),
                            }}
                            label={showTimeRange ? 'Until' : 'Due Date'}
                            name="DateTimeEnd"
                            disabled
                            time={field['DeadlineType'].showTime}
                            help="When is the task due."
                />
            </Col>
            </Row>)}
        </div>
        );
    }

    renderTaskMainComponent() {
        const {
            disabled,
            editMode = false,
            renderGoToCaseButton = true,
        } = this.props;

        let taskMain;
        const field = this.state.field;
        if (editMode) {
            taskMain = (
                <div>
                {field['ActionError'] && <ServerError error={field['ActionError']} />}
                {!this.props.disableCheckBox && (
                <Row>
                <Col xs={12}>
                    <CheckBox field={{
                                  value: field.Completed,
                                  onChange: this.onChange,
                                  name: 'Completed',
                              }}
                              disabled={disabled || !isEmptyObject(field.error) || field['ActionTrigger'] === 'due_datetime'}
                              label="Done"
                              name="Completed"
                              checked={field.Completed}
                              onChange={this.onChange} 
                    />
                </Col>
                </Row>)}
                <Row>
                <Col xs={12} id="tour-case-action-task-label">
                    <TextInput label="Task"
                               field={{
                                   value: field.Title,
                                   onChange: this.onChange,
                                   name: 'Title',
                               }}
                               disabled={disabled} 
                    />
                </Col>
                </Row>
                </div>
            );
        } else {
            let handle = '';
            if (!isEmptyObject(field.error)) {
                handle = (
                    <InputGroup.Addon style={{ backgroundColor: 'red' }}>
                        <Glyphicon glyph="remove" />
                    </InputGroup.Addon>);
            } else if (!this.props.disableMove) {
                handle = (
                    <InputGroup.Addon>
                        <Glyphicon glyph="sort" />
                    </InputGroup.Addon>);
            }

            taskMain = (
                <Row>
                    <Col xs={12}>
                    <InputGroup>
                        {handle}
                        {!this.props.disableCheckBox && (
                        <InputGroup.Addon>
                            <input type="checkbox"
                                   disabled={disabled || !isEmptyObject(field.error) || field['ActionTrigger'] === 'due_datetime'}
                                   name="Completed"
                                   checked={field.Completed}
                                   onChange={this.onChange}
                            />
                        </InputGroup.Addon>)}
                        <input type="text"
                               className="form-control"
                               name="Title"
                               disabled={disabled}
                               defaultValue={field.Title}
                               value={field.Title}
                               onChange={this.onChange}
                               onFocus={() => {
                                    if (this.props.disableMove) {
                                        return;
                                    }
    
                                    this.collapseFields(field);
                               }}
                        />
                        {(renderGoToCaseButton || !editMode || this.props.onDelete) && (
                            <InputGroup.Button>
                            {field['CaseId'] && renderGoToCaseButton && (
                            <Button disabled={disabled || field['CaseId'] == undefined}
                                    onClick={this.onCaseIdNavigate}
                                    icon="arrow-right"
                                    iconStyle={{ paddingBottom: '5px' }}
                                    tooltip="Go to the case related to this task"
                                    tooltipPosition="left" 
                            />)}
                            {!editMode && field['$Editing$'] && (
                            <Button disabled={disabled}
                                    icon="triangle-bottom"
                                    onClick={(evt) => {
                                        evt.preventDefault();
                                        this.collapseFields(field, true);
                                    }}
                                    bsStyle="link"
                                    iconStyle={{paddingBottom: '5px'}}
                                    tooltip="Show additional details and settings"
                                    tooltipPosition="left" 
                            />)}
                            </InputGroup.Button>)}
                    </InputGroup>
                    </Col>
                </Row>
            );
        }

        return taskMain;
    }

    renderDetailsComponent() {
        const {
            disabled,
            tasks,
            loggedIn,
            employees = [],
        } = this.props;

        const field = this.state.field;
        const fieldIndex = tasks.findIndex(task => task.id == field.id);
        const when = this.renderWhenComponent();
        const blockedByOptions = tasks.map((task, index) => ({
                value: task.id,
                label: task['Title'],
                completed: task['Completed'],
                index,
        })).filter(task => ((fieldIndex != -1 && task.index < fieldIndex) || (fieldIndex == -1 && task.value !== field.id)) && !task.completed);

        blockedByOptions.unshift({
            value: 0,
            label: '',
        });

        let caseManagerOptions = [];
        caseManagerOptions = caseManagerOptions.concat(employees);
        caseManagerOptions.unshift({label: '', value: -1});

        const details = (
            <div>
                <Row>
                <Col xs={12} id="tour-case-action-task-blockedby">
                    <Select field={{
                                name: 'AssignedTo',
                                error: field.error ? field.error['AssignedTo'] : undefined,
                                touched: true,
                                value: field['AssignedTo'],
                                onChange: this.onChange,
                            }}
                            label="Assigned To"
                            name="AssignedTo"
                            help="Select who will be responsible for completing given task."
                            disabled={disabled}
                            options={caseManagerOptions.map(wrapEmployeeForSelect)}
                            onChange={this.onChange} 
                    />
                </Col>
                </Row>
                {when}
                <Row>
                    <Col xs={12} id="tour-case-action-task-description">
                        <TextArea field={{
                                        name: 'Description',
                                        error: field.error ? field.error['Description'] : undefined,
                                        touched: true,
                                        value: field['Description'],
                                        onChange: this.onChange,
                                   }}
                                   disabled={disabled}
                                   label="Details"
                                   rows={3}
                                   name="Description"
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} id="tour-case-action-task-blockedby">
                        <Select field={{
                                    name: 'BlockedBy',
                                    error: field.error ? field.error['BlockedBy'] : undefined,
                                    touched: true,
                                    value: parseInt(field['BlockedBy']),
                                    onChange: this.onChange,
                                }}
                                label="Blocked By"
                                name="BlockedBy"
                                help="Select the task that will hide this task unless the selected task gets completed or deleted. You can also use task filter to view this by default hidden task."
                                disabled={disabled}
                                options={blockedByOptions}
                                onChange={this.onChange} 
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} id="tour-case-action-task-action">
                        <Select field={{
                                    name: 'ActionType',
                                    error: field.error ? field.error['ActionType'] : undefined,
                                    touched: true,
                                    value: field['ActionType'] || 'none',
                                    onChange: this.onChange,
                                }}
                                label="Action"
                                name="ActionType"
                                help="Select the automatic action that shold happen automatically."
                                disabled={disabled}
                                options={[
                                    { label: 'None', value: 'none' },
                                    { label: 'Set Case State', value: 'set_case_state' },
                                    { label: 'Send Email', value: 'send_email' },
                                ]}
                                onChange={this.onChange} 
                        />
                    </Col>
                </Row>
                {/* field['ActionType'] !== 'none' && (
                    <Row>
                    <Col xs={12}>
                        <Select field={{
                                    name: 'ActionTrigger',
                                    error: field.error ? field.error['ActionTrigger'] : undefined,
                                    touched: true,
                                    value: field['ActionTrigger'],
                                    onChange: this.onChange
                                }}
                                label='Action Trigger'
                                name='ActionTrigger'
                                help='Select when should the action be performed.'
                                disabled={true}
                                options={actionTriggerOptions}
                                onChange={this.onChange}/>
                    </Col>
                </Row>
                )*/}
                {field['ActionType'] == 'set_case_state' && (
                    <Row>
                    <Col xs={12}>
                        <Select field={{
                                    name: 'ActionCaseState',
                                    error: field.error ? field.error['ActionCaseState'] : undefined,
                                    touched: true,
                                    value: field['ActionCaseState'],
                                    onChange: this.onChange,
                                }}
                                label="Set Case State To"
                                name="ActionCaseState"
                                help="Select when should the."
                                disabled={disabled}
                                options={this.props.caseStates || []}
                                onChange={this.onChange} 
                        />
                    </Col>
                    </Row>
                )}
                {field['ActionType'] === 'send_email' && (
                    <SendEmailForm renderTemplateLoad={false}
                                   enableAddTemplate={false}
                                   enableEditTemplate={false}
                                   renderEmailSection
                                   renderEmailButton={false}
                                   renderAsRegularForm
                                   renderAttachmentField={false}
                                   doNotTransform
                                   templatesFieldName="notesEmailTemplates"
                                   disableCaseFilesAutoload
                                   disableEmailFrom
                                   maxWidth
                                   onChange={(email) => {
                                       this.onChange({
                                           target: {
                                               value: email,
                                               name: 'ActionEmail',
                                           },
                                       });
                                   }}
                                   initialEmail={field['ActionEmail']}
                    />
                )}
            </div>
        );

        return details;
    }

    collapseFields(field, toggle = false) {
        if (!this.props.collapseFields) {
            return;
        }

        const f = { ...field };
        f['$Editing$'] = toggle ? !f['$Editing$'] : true;
        this.setState({
            hasFocus: f['$Editing$'],
            field: f
        });

        this.props.collapseFields(f);
    }    

    render() {
        const {
            isDragging,
            connectDragSource,
            connectDropTarget,
            connectDragPreview,
            disabled,
            editMode = false,
            label,
            actionLabel,
            onActionBtnClicked,
        } = this.props;

        const field = this.state.field;
        let details;
        const getDetails = () => {
            if (!details) {
                details = this.renderDetailsComponent();
            }

            return details;
        }


        let taskMain;
        const getTaskMain = () => {
            if (!taskMain) {
                taskMain = this.renderTaskMainComponent();
            }

            return taskMain;
        }

        let actionBtn = '';
        if (onActionBtnClicked) {
            actionBtn = (
                <Row style={{ float: 'right', marginRight: 5 }}>
                <Col xs={12}>
                <Button label={actionLabel}
                        onClick={() => {
                            onActionBtnClicked(field);
                        }}
                        id="tour-case-action-task-add"
                        bsStyle="primary"
                        style={{ width: 150 }} 
                />
                </Col>
                </Row>
            );
        }

        if (field['$Editing$'] && !isDragging) {
            taskMain = (
                <div>
                {getTaskMain()}
                <Panel style={{ marginLeft: 3, marginRight: 3, backgroundColor: '#F5F5F5' }}>
                {getDetails()}
                {actionBtn}
                {this.props.onDelete && (
                    <Row>
                    <Button onClick={() => {
                                this.props.onDelete(field);
                            }}
                            disabled={disabled}
                            icon="trash"
                            iconStyle={{paddingBottom: '5px', marginLeft: 10}}
                            style={{float: 'left'}}
                            tooltip="Remove task"
                            tooltipPosition="left"
                            label="Delete"
                            bsStyle="link"
                    />
                    </Row>)}
                </Panel>
                {this.props.onDelete && (
                <div style={{marginTop: -22, marginBottom: 10, textAlign: 'center'}}>
                <Button onClick={() => {
                            this.props.onAdd(field);
                        }}
                        disabled={disabled}
                        icon="plus"
                        bsStyle="link"
                        label="Add Task"
                        iconStyle={{paddingBottom: '5px'}}
                        tooltip="Add new task"
                        tooltipPosition="left"
                />
                </div>)}
                </div>
            );
        } else if (editMode) {
            taskMain = (
                <div>
                {getTaskMain()}
                {getDetails()}
                {actionBtn}
                </div>
            );

            if (!this.props.showAsDialog) {
                taskMain = <Panel header={label}>{getTaskMain()}</Panel>;
            }
        }

        if (this.props.showAsDialog) {
            let backgroundColor = dialogHeaderFooterBgColor;
            if (!isEmptyObject(field.error)) {
                backgroundColor = errorBackgroundColor;
            }

            taskMain = (
                <Modal show={this.props.dialogOpen}
                       onHide={this.props.dialogCancel}
                       backdrop='static'
                >
                    <Modal.Header closeButton style={{ backgroundColor }}>
                        <Modal.Title>Edit Task</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <Row>
                        <Col xs={12}>
                        {getTaskMain()}
                        </Col>
                    </Row>
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor }}>
                    <Button style={{ float: 'right', marginLeft: 25 }}
                            label="Save"
                            icon="floppy-disk"
                            bsStyle="primary"
                            disabled={disabled || !isEmptyObject(field.error)}
                            loading={this.state.loading}
                            onClick={() => {
                                const task = { ...this.state.field };
                                task['$Modified$'] = true;
                                const modifiedTasks = [task];
                                if (!validateTaskField(modifiedTasks)) {
                                    return;
                                }
                                this.setState({ loading: true });
                                updateTask(task, this.props, () => {
                                    this.setState({ loading: false });
                                    this.props.dialogConfirm(task);
                                }, (error) => {
                                    console.error(new Error().stack, error);
                                    this.props.dispatch(caseSetServerError(error));
                                    this.setState({ loading: false });
                                });
                            }} />
                    <Button style={{float: 'left'}}
                            icon="trash"
                            label="Delete"
                            disabled={disabled}
                            loading={this.state.loading}
                            onClick={() => {
                                const task = { ...this.state.field };
                                task['$Deleted$'] = true;
                                task['$Modified$'] = true;
                                this.setState({ loading: true });
                                updateTask(
                                    task,
                                    this.props,
                                    () => {
                                        this.setState({ loading: false });
                                        this.props.dialogConfirm();
                                    },
                                    error => {
                                        console.error(new Error().stack, error);
                                        this.props.dispatch(caseSetServerError(error));
                                        this.setState({ loading: false });
                                    }
                                );
                            }} />
                    </Modal.Footer>
                </Modal>
            );
        }

        let cds = connectDragSource;
        if (this.props.disableMove) {
            cds = (x) => x;
        }

        return connectDropTarget(cds(
            <div style={{ opacity: isDragging ? 0.5 : 1, cursor: 'grab' }}
                 onMouseDown={() => {
                     if (!this.state.hasFocus) {
                        this.collapseFields(field);
                     }
                 }}
            >
            {getTaskMain()}
            </div>,
        ));
    }
}

TaskEditorField.propTypes = {
    onDelete: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    initialField: PropTypes.object.isRequired,
    isEmptyChronoError: PropTypes.bool,
    disabled: PropTypes.bool,
    tasks: PropTypes.array.isRequired,
    disableMove: PropTypes.boolean,
    disableTimeHint: PropTypes.boolean,
    disableCheckBox: PropTypes.boolean,
    caseId: PropTypes.int,
    editMode: PropTypes.bool,
    renderGoToCaseButton: PropTypes.bool,
    label: PropTypes.string,
    actionLabel: PropTypes.string,
    onActionBtnClicked: PropTypes.func,
    caseType: PropTypes.string,
    currentCase: PropTypes.object,

    // Dialog
    showAsDialog: PropTypes.boolean,
    dialogCancel: PropTypes.func,
    dialogConfirm: PropTypes.func,
    dialogOpen: PropTypes.bool,

    moveField: PropTypes.func,
    findField: PropTypes.func,
    collapseFields: PropTypes.func,

    // React DnD:
    id: PropTypes.string,
    isDragging: PropTypes.bool.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    connectDragPreview: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    const currentConfig = state.user.config;
    let dateFields = [];
    if (currentConfig && ownProps.caseType) {
        const dateTimeFields = extractFields(currentConfig.workflow, { CaseType: ownProps.caseType }, 'DateTimeInput').map((field) => {
            field['value'] = `${field['$TableName$']}.${field.name}`;
            field['label'] = `${field.props.label} (${field['$TableName$']})`;
            return field;
        });

        dateFields = extractFields(currentConfig.workflow, { CaseType: ownProps.caseType }, 'DateInput').map((field) => {
            field['value'] = `${field['$TableName$']}.${field.name}`;
            field['label'] = `${field.props.label} (${field['$TableName$']})`;
            return field;
        });
        
        dateFields = [ ...dateFields, ...dateTimeFields ].sort((a, b) => a['$TableName$'].localeCompare(b['$TableName$']));
    }

    const caseStates = (currentConfig && currentConfig.caseStates && currentConfig.caseStates.map(v => v.type)) || [];
    dateFields.unshift({
        name: 'CreatedDate',
        'type': 'DateInput',
        props: {
            label: 'Task Creation',
        },
        'value': 'task.CreatedDate',
        label: 'Task Creation',
        $TableName$: 'task',
    });

    return {
        dateFields,
        caseStates,
        loggedIn: state.user.loggedIn,
        employees: state.user.employees,
    };
};

export default _.flow(
  DragSource(TASK_FIELD, fieldSource, collectSource),
  DropTarget(TASK_FIELD, fieldTarget, collectTarget),
  connect(mapStateToProps),
)(TaskEditorField);
